import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Component, Inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { CBox_PublicPaginatedResponse, CBox_PublicSuccessResponse } from '@server/services/cbox/public/api/v1/resources/common/request_base/types';
import { CBox_AdminEquipmentListItem, CBox_AdminEquipmentListResponse, CBox_EquipmentType } from '@server/services/cbox/public/api/v1/resources/internal/equipment/types';
import { CBox_AdminActionAttachEquipmentToModuleRequestData, CBox_AdminLockerModuleDataResponse, CBox_AdminModuleListItemDataResponse } from '@server/services/cbox/public/api/v1/resources/internal/locker_module/types';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ApiService, handlePublicApiError } from 'src/services/api/api.service';
import { ToastService } from 'src/services/toast/toast.service';

@Component({
  selector: 'app-cbox',
  templateUrl: './cbox-admin-link-necesarry-equipment-to-locker-module-dialog.component.html',
  styleUrls: ["./cbox-admin-link-necesarry-equipment-to-locker-module-dialog.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    DropdownModule,
    ButtonModule,
    ProgressSpinnerModule,
    ReactiveFormsModule,
    InputTextModule,
    InputTextareaModule
  ]
})

export class CBoxAdminLinkEquipmentToLockerModuleDialogComponent implements OnInit {

  modules = signal<CBox_AdminModuleListItemDataResponse[]>([]);
  modulesFetched = signal(false);
  page = signal(1);
  equipmentTypes = signal<string[] | undefined>(undefined);
  equipments = signal<CBox_AdminEquipmentListResponse | undefined>(undefined);
  loading = signal(true);
  form: FormGroup;

  constructor(
    private api: ApiService,
    private toastService: ToastService,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CBoxAdminLinkEquipmentToLockerModuleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      equipmentType: CBox_AdminLockerModuleDataResponse["equipmentTypeCounts"][0]["definition"];
      module?: {
        id: string;
        serial: string;
      };
    }) {
      this.form = this.fb.group({
        equipmentType: [{
          value: undefined,
          disabled: true
        }, Validators.required],
        equipment: [undefined],
        actionPerformedBy: ["", Validators.required],
        moduleId: [undefined, Validators.required],
        notes: [""]
      });
      this.equipmentTypes.set([this.data.equipmentType.name]);
      this.form.get("equipmentType")?.patchValue(this.data.equipmentType.name);
    }

  ngOnInit(): void {
    this.init();
  }

  link(): void {
    this.api.post("backend/internal/equipments/attach", this.getLinkStructure()).subscribe((response) => {
      this.toastService.showSuccessToast("Confirmare", "Echipamentul a fost asociat cu succes.");
      this.dialogRef.close();
      this.loading.set(false);
    }, (e: HttpErrorResponse) => {
      this.loading.set(false);
      handlePublicApiError(e, this.toastService);
    });
  }

  private async init(): Promise<void> {
    this.fetchModules(this.page());
    this.fetchEquipments();
    this.equipmentTypes.set([this.data.equipmentType.name]);
  }

  private fetchEquipments(): void {
    const params = new HttpParams({
      fromObject: {
        typeId: this.data.equipmentType.typeId,
        includeInstalled: false,
        includeNotInstalled: true
      }
    });
    this.api.get<CBox_PublicSuccessResponse<CBox_AdminEquipmentListItem[]>>("backend/internal/equipments/list", params).subscribe((response) => {
      this.equipments.set(response.data);
      this.loading.set(false);
    }, (e: HttpErrorResponse) => {
      this.loading.set(false);
      handlePublicApiError(e, this.toastService);
    });
  }

  private fetchModules(pageNumber?: number): void {
    let httpParams = new HttpParams().append("page", this.page());
    this.api.get<CBox_PublicPaginatedResponse<CBox_AdminModuleListItemDataResponse>>("backend/internal/modules/list", httpParams).subscribe((response) => {
      // A NEW array on every page: `[options]` on the module dropdown only
      // re-binds when the reference changes, and PrimeNG caches its label on
      // the options it was last given. Pushing into the same array left the
      // dropdown showing its placeholder after a module had been chosen.
      this.modules.update(modules => [...(modules ?? []), ...response.data]);
      if (response.totalCount > this.modules()!.length) {
        this.page.update(page => page + 1);
        this.fetchModules(this.page() + 1);
        return;
      }
      if (this.data.module?.id) {
        const moduleId = this.form.get("moduleId");
        moduleId?.patchValue(this.data.module?.id);
        moduleId?.disable();
      }
      this.modulesFetched.set(true);
      this.loading.set(false);
    }, (e: HttpErrorResponse) => {
      this.loading.set(false);
      handlePublicApiError(e, this.toastService);
    });
  }

  private getLinkStructure(): CBox_AdminActionAttachEquipmentToModuleRequestData {
    const f = this.form;
    const data = {
      moduleId: f.get("moduleId")?.value,
      equipmentId: f.get("equipment")?.value.id,
      actionPerformedBy: f.get("actionPerformedBy")?.value,
      notes: f.get("notes")?.value
    }

    return data;
  }
}